import React, { useState } from 'react';
import { Button, Modal, message } from 'antd';
import { PaperClipOutlined } from '@ant-design/icons';
import { useTranslation } from 'react-i18next';
import styled from 'styled-components';
import { FileUploadArea, FileUploadItem } from '../features/file-upload';
import { FileUploadService } from '../features/file-upload/services/fileUploadService';

// 与ChatPageNew.tsx保持一致的ActionButton样式
const ActionButton = styled(Button)`
  &.ant-btn {
    border: none;
    background: transparent;
    color: var(--text-secondary, #8e8ea0);
    width: 32px;
    height: 32px;
    border-radius: 16px;
    display: flex;
    align-items: center;
    justify-content: center;
    transition: all 0.2s ease;
    padding: 0;
    
    &:hover {
      background: var(--bg-hover, #f3f4f6);
      color: var(--text-primary, #2d3748);
      transform: scale(1.05);
    }
    
    &:active {
      transform: scale(0.95);
    }
    
    &:focus {
      background: var(--bg-hover, #f3f4f6);
      color: var(--text-primary, #2d3748);
    }
    
    &:disabled {
      color: var(--text-muted, #9ca3af);
      cursor: not-allowed;
      transform: none;
    }
  }
`;

interface FileUploadIntegrationProps {
  onFilesUploaded?: (files: FileUploadItem[], results: any[]) => void;
  disabled?: boolean;
  maxFiles?: number;
  maxFileSize?: number;
  userQuery?: string;
  userId?: string;
  sessionId?: string;
}

/**
 * 文件上传集成组件
 * 在聊天界面中提供完整的文件上传功能
 */
const FileUploadIntegration: React.FC<FileUploadIntegrationProps> = ({
  onFilesUploaded,
  disabled = false,
  maxFiles = 5,
  maxFileSize = 10 * 1024 * 1024, // 10MB
  userQuery = '',
  userId = 'anonymous',
  sessionId
}) => {
  const { i18n } = useTranslation();
  const [modalVisible, setModalVisible] = useState(false);
  const [uploading, setUploading] = useState(false);

  /**
   * 处理文件上传
   */
  const handleUpload = async (files: File[]) => {
    if (files.length === 0) return;

    setUploading(true);
    
    try {
      // 显示上传开始消息
      message.loading('正在上传文件...', 0);

      // 调用文件上传服务
      const results = await FileUploadService.uploadFiles(files, {
        userQuery,
        userId,
        sessionId,
        language: i18n.language,
        onProgress: (progress) => {
          // console.log(`上传进度: ${progress}%`);
        }
      });

      // 创建文件项
      const fileItems: FileUploadItem[] = files.map((file, index) => ({
        id: `upload-${Date.now()}-${index}`,
        file,
        type: FileUploadService.getFileCategory(file) as any,
        url: FileUploadService.createPreviewUrl(file),
        status: results[index]?.success ? 'success' : 'error'
      }));

      // 检查上传结果
      const successCount = results.filter(r => r.success).length;
      const failCount = results.length - successCount;

      message.destroy();

      if (successCount > 0) {
        message.success(`成功上传 ${successCount} 个文件${failCount > 0 ? `，${failCount} 个失败` : ''}`);
        
        // 🔥 重要：立即关闭模态框，避免用户看到文件列表
        setModalVisible(false);
        
        // 调用回调函数，传递上传结果
        onFilesUploaded?.(fileItems, results);
      } else {
        message.error('所有文件上传失败');
      }

    } catch (error) {
      message.destroy();
      message.error('文件上传失败');
      console.error('文件上传错误:', error);
    } finally {
      setUploading(false);
    }
  };

  return (
    <>
      {/* 文件上传按钮 */}
      <ActionButton
        type="text"
        icon={<PaperClipOutlined />}
        onClick={() => setModalVisible(true)}
        disabled={disabled}
        title="上传文件"
      />

      {/* 文件上传模态框 */}
      <Modal
        title="📎 文件上传"
        open={modalVisible}
        onCancel={() => setModalVisible(false)}
        footer={null}
        width={600}
        destroyOnHidden
      >
        <FileUploadArea
          maxFiles={maxFiles}
          maxFileSize={maxFileSize}
          onUpload={handleUpload}
          disabled={uploading}
          uploadText="点击或拖拽文件到此处"
          uploadHint={`支持图片、音频、PDF文件，最大${Math.floor(maxFileSize / 1024 / 1024)}MB`}
          dragText="释放文件开始上传"
          onFileAdd={(file) => {
            // console.log('添加文件:', file.file.name);
          }}
          onFileRemove={(fileId) => {
            // console.log('删除文件:', fileId);
          }}
          onValidationError={(error) => {
            message.error(error);
          }}
        />
      </Modal>
    </>
  );
};

export default FileUploadIntegration; 